// Removed Sentry dependency for decoupled bundle monitoring

interface BundleMetrics {
  bundleSize: number;
  chunkSizes: Record<string, number>;
  loadTime: number;
  timestamp: number;
}

class BundleMonitor {
  private metrics: BundleMetrics[] = [];
  private readonly MAX_BUNDLE_SIZE = 200 * 1024; // 200KB (stricter)
  private readonly MAX_TOTAL_SIZE = 400 * 1024; // 400KB (stricter)
  private readonly MAX_CHART_BUNDLE = 150 * 1024; // 150KB (chart bundle limit)

  init() {
    try {
      console.log("[BundleMonitor] Initializing...");
      this.monitorResourceTiming();
      this.trackInitialLoad();
      console.log("[BundleMonitor] Initialization complete ✅");
    } catch (err) {
      console.error("[BundleMonitor] Failed to initialize ❌", err);
    }
  }

  private monitorResourceTiming() {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.name.includes('.js') && entry.name.includes('assets')) {
            this.trackResourceLoad(entry as PerformanceResourceTiming);
          }
        });
      });
      
      observer.observe({ entryTypes: ['resource'] });
    }
  }

  private trackResourceLoad(entry: PerformanceResourceTiming) {
    const size = entry.transferSize || 0;
    const loadTime = entry.responseEnd - entry.requestStart;
    
    // Extract chunk name from file path
    const match = entry.name.match(/assets\/(.+?)-[a-f0-9]+\.js/);
    const chunkName = match ? match[1] : 'unknown';
    
    const metric: BundleMetrics = {
      bundleSize: size,
      chunkSizes: { [chunkName]: size },
      loadTime,
      timestamp: Date.now()
    };
    
    this.metrics.push(metric);
    this.checkBundleBudgets(metric);
  }

  private trackInitialLoad() {
    window.addEventListener('load', () => {
      const totalSize = this.getTotalBundleSize();
      
      if (totalSize > this.MAX_TOTAL_SIZE) {
        this.reportBudgetViolation('total_bundle_size', totalSize, this.MAX_TOTAL_SIZE);
      }
    });
  }

  private checkBundleBudgets(metric: BundleMetrics) {
    if (metric.bundleSize > this.MAX_BUNDLE_SIZE) {
      this.reportBudgetViolation('individual_bundle_size', metric.bundleSize, this.MAX_BUNDLE_SIZE);
    }
  }

  private reportBudgetViolation(type: string, actual: number, limit: number) {
    const message = `Bundle size violation: ${type} exceeded. Actual: ${(actual / 1024).toFixed(1)}KB, Limit: ${(limit / 1024).toFixed(1)}KB`;
    
    console.warn('[BundleMonitor]', message);
    
    // Lightweight local logging without external dependencies
    const violation = {
      type,
      actual_size: actual,
      limit_size: limit,
      violation_percentage: ((actual - limit) / limit * 100).toFixed(1),
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent
    };
    
    console.warn('[BundleMonitor] Violation details:', violation);
    
    // Store in localStorage for debugging if available
    try {
      const violations = JSON.parse(localStorage.getItem('bundle_violations') || '[]');
      violations.push(violation);
      localStorage.setItem('bundle_violations', JSON.stringify(violations.slice(-10))); // Keep last 10
    } catch (e) {
      console.warn('[BundleMonitor] Could not store violation in localStorage:', e);
    }
  }

  getTotalBundleSize(): number {
    return this.metrics.reduce((total, metric) => total + metric.bundleSize, 0);
  }

  getMetrics(): BundleMetrics[] {
    return [...this.metrics];
  }

  getBundleSizeReport(): Record<string, number> {
    const report: Record<string, number> = {};
    
    this.metrics.forEach(metric => {
      Object.entries(metric.chunkSizes).forEach(([chunk, size]) => {
        report[chunk] = (report[chunk] || 0) + size;
      });
    });
    
    return report;
  }
}

export const bundleMonitor = new BundleMonitor();